import {DI_TOKENS} from '@/di/di.tokens';
import {DailyGameInputLetterStatus} from '@/stores/DailyGameStore/DailyGameStore.types';

import {injected} from 'brandi';

import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {NavigationClickerModalService} from '@/services/NavigationClickerModalService/NavigationClickerModalService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {DailyGameStore} from '@/stores/DailyGameStore/DailyGameStore';
import {UserStore} from '@/stores/UserStore/UserStore';
import {ClickerAudioPlayer, ClickerSoundsBundle} from '@/ui/pages/ClickerGamePage/clicker-game/ClickerAudioPlayer';

export class DailyGameService {
  constructor(
    private readonly dailyGameStore: DailyGameStore,
    private readonly userStore: UserStore,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly projectApi: IProjectApi,
    private readonly navigationClickerModalService: NavigationClickerModalService,
    private readonly audioPlayer: ClickerAudioPlayer,
    private readonly trackingService: TrackingService,
    private readonly platformService: PlatformService,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
  ) {}

  public async fetchDailyGameConfig(): Promise<void> {
    try {
      const response = await this.projectApi.getDailyGameConfig();

      this.dailyGameStore.setConfig(response.payload);
    } catch (error) {
      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      }
    }
  }

  public async checkDailyGameCipher(cipher: string, key: number): Promise<void> {
    try {
      this.audioPlayer.playClickSound(ClickerSoundsBundle.Tap);

      this.dailyGameStore.setLoadingFetchStatus();
      this.dailyGameStore.setInputItemStatus(key, DailyGameInputLetterStatus.Fetching);

      const response = await this.projectApi.checkDailyGameCipher(cipher);
      const {isFullCipher, isValid} = response.payload;

      if (isValid) {
        this.dailyGameStore.setInputItemStatus(key, DailyGameInputLetterStatus.Valid);
      } else {
        this.dailyGameStore.setInputItemStatus(key, DailyGameInputLetterStatus.Invalid);
        this.dailyGameStore.setWasFail();
        this.audioPlayer.playClickSound(ClickerSoundsBundle.RewardWrongTap);
      }

      if (isFullCipher) {
        this.userStore.completeDailyGame();
        this.audioPlayer.playClickSound(ClickerSoundsBundle.DailyGameWin);
      }

      this.dailyGameStore.setSuccessFetchStatus();
    } catch (e) {
      if (this.errorsService.isErrorRecalculationLeagues(e)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        this.dailyGameStore.setErrorFetchStatus();

        throw e;
      }
    }
  }

  public getCtaLink(): string {
    const cta = this.dailyGameStore.getState().config.cta;

    if (this.platformService.isInvesting() && cta.investDeeplink) {
      return cta.investDeeplink;
    }

    if (this.platformService.isMb() && cta.deeplink) {
      return cta.deeplink;
    }

    return cta.link;
  }

  public claimRewards = (): void => {
    this.clickerStateStore.addPoints(this.dailyGameStore.getReward());
  };

  public openDailyGame(): void {
    this.navigationClickerModalService.setCurrentModalTypeDaily();
  }

  public closeDailyGame(): void {
    this.navigationClickerModalService.clearCurrentModalType();
  }

  public trackDailyGameCompletedIfWasNotFails = (): void => {
    if (!this.dailyGameStore.isWasFails()) {
      this.trackingService.trackCipherDailyGameSuccess();
    }
  };
}

injected(
  DailyGameService,
  DI_TOKENS.dailyGameStore,
  DI_TOKENS.userStore,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.projectApi,
  DI_TOKENS.navigationClickerModalService,
  DI_TOKENS.clickerGameAudioPlayer,
  DI_TOKENS.trackingService,
  DI_TOKENS.platformService,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
);
